import { useState, useEffect } from "react";
import { Loader2, Building2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { Subcontractor } from "@/pages/Subcontractors";

interface SubcontractorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subcontractor: Subcontractor | null;
  onSuccess: () => void;
}

const SubcontractorDialog = ({
  open,
  onOpenChange,
  subcontractor,
  onSuccess,
}: SubcontractorDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: "",
    siret: "",
    siren: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    status: "active",
    notes: "",
  });

  useEffect(() => {
    if (subcontractor) {
      setFormData({
        company_name: subcontractor.company_name || "",
        siret: subcontractor.siret || "",
        siren: subcontractor.siren || "",
        contact_name: subcontractor.contact_name || "",
        contact_email: subcontractor.contact_email || "",
        contact_phone: subcontractor.contact_phone || "",
        address: subcontractor.address || "",
        status: subcontractor.status || "active",
        notes: subcontractor.notes || "",
      });
    } else {
      setFormData({
        company_name: "",
        siret: "",
        siren: "",
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        address: "",
        status: "active",
        notes: "",
      });
    }
  }, [subcontractor, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.company_name.trim()) {
      toast({
        title: "Erreur",
        description: "Le nom de l'entreprise est requis",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      if (subcontractor) {
        // Update
        const { error } = await supabase
          .from("subcontractors")
          .update({
            company_name: formData.company_name,
            siret: formData.siret || null,
            siren: formData.siren || null,
            contact_name: formData.contact_name || null,
            contact_email: formData.contact_email || null,
            contact_phone: formData.contact_phone || null,
            address: formData.address || null,
            status: formData.status as any,
            notes: formData.notes || null,
          })
          .eq("id", subcontractor.id);

        if (error) throw error;
        toast({
          title: "Prestataire mis à jour",
          description: "Les informations ont été enregistrées",
        });
      } else {
        // Create
        const { error } = await supabase
          .from("subcontractors")
          .insert({
            user_id: user.id,
            company_name: formData.company_name,
            siret: formData.siret || null,
            siren: formData.siren || null,
            contact_name: formData.contact_name || null,
            contact_email: formData.contact_email || null,
            contact_phone: formData.contact_phone || null,
            address: formData.address || null,
            status: formData.status as any,
            notes: formData.notes || null,
            compliance_score: 0,
          });

        if (error) throw error;
        toast({
          title: "Prestataire ajouté",
          description: `${formData.company_name} a été ajouté à votre liste`,
        });
      }

      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      console.error("Submit error:", error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'enregistrer le prestataire",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            {subcontractor ? "Modifier le prestataire" : "Nouveau prestataire"}
          </DialogTitle>
          <DialogDescription>
            {subcontractor
              ? "Modifiez les informations du prestataire"
              : "Ajoutez un nouveau sous-traitant ou prestataire"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Company Name */}
          <div className="space-y-2">
            <Label htmlFor="company_name">Nom de l'entreprise *</Label>
            <Input
              id="company_name"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              placeholder="Ex: Dupont Construction"
              required
            />
          </div>

          {/* SIRET / SIREN */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="siret">SIRET</Label>
              <Input
                id="siret"
                value={formData.siret}
                onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                placeholder="12345678901234"
                maxLength={14}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="siren">SIREN</Label>
              <Input
                id="siren"
                value={formData.siren}
                onChange={(e) => setFormData({ ...formData, siren: e.target.value })}
                placeholder="123456789"
                maxLength={9}
              />
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-2">
            <Label htmlFor="contact_name">Nom du contact</Label>
            <Input
              id="contact_name"
              value={formData.contact_name}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              placeholder="Jean Dupont"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="contact_email">Email</Label>
              <Input
                id="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                placeholder="contact@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_phone">Téléphone</Label>
              <Input
                id="contact_phone"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                placeholder="01 23 45 67 89"
              />
            </div>
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label htmlFor="address">Adresse</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="123 rue Example, 75001 Paris"
            />
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status">Statut</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Actif</SelectItem>
                <SelectItem value="inactive">Inactif</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="blocked">Bloqué</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Informations complémentaires..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : subcontractor ? (
                "Enregistrer"
              ) : (
                "Ajouter"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SubcontractorDialog;
